import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button } from '@saas-kit/ui';
import {
  DataTable,
  type DataTableColumn,
  type PaginationState,
} from '@saas-kit/ui/DataTable';

import type { Announcement } from './api';

const STATUS_VARIANT: Record<
  string,
  'secondary' | 'success' | 'destructive' | 'outline'
> = {
  draft: 'secondary',
  queued: 'outline',
  completed: 'success',
  partial_failed: 'destructive',
};

interface Props {
  announcements: Announcement[];
  pagination: PaginationState;
  isLoading: boolean;
  error: string | null;
  onView: (announcement: Announcement) => void;
}

export function AnnouncementsTable({
  announcements,
  pagination,
  isLoading,
  error,
  onView,
}: Props): JSX.Element {
  const { t, i18n } = useTranslation('adminAnnouncements');

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  const columns = useMemo<DataTableColumn<Announcement>[]>(
    () => [
      {
        id: 'subject',
        header: t('table.subject'),
        cell: (row) => (
          <button
            type="button"
            onClick={() => onView(row)}
            className="text-start font-medium text-primary underline-offset-2 hover:underline"
          >
            {row.subject}
          </button>
        ),
        exportValue: (row) => row.subject,
      },
      {
        id: 'audience',
        header: t('table.audience'),
        cell: (row) => t(`audience.${row.audience_kind}`),
        exportValue: (row) => row.audience_kind,
        hideOnMobile: true,
      },
      {
        id: 'channels',
        header: t('table.channels'),
        cell: (row) => row.channels.map((c) => t(`channels.${c}`)).join(', '),
        exportValue: (row) => row.channels.join(' '),
        hideOnMobile: true,
      },
      {
        id: 'status',
        header: t('table.status'),
        cell: (row) => (
          <Badge variant={STATUS_VARIANT[row.status] ?? 'secondary'}>
            {t(`status.${row.status}`, { defaultValue: row.status })}
          </Badge>
        ),
        exportValue: (row) => row.status,
      },
      {
        id: 'delivered',
        header: t('table.delivered'),
        align: 'end',
        cell: (row) => row.delivered_count,
      },
      {
        id: 'failed',
        header: t('table.failed'),
        align: 'end',
        cell: (row) => row.failed_count,
      },
      {
        id: 'created_at',
        header: t('table.created_at'),
        cell: (row) => dateFormatter.format(new Date(row.created_at)),
        exportValue: (row) => row.created_at,
        hideOnMobile: true,
      },
      {
        id: 'actions',
        header: t('table.actions'),
        align: 'end',
        exportSkip: true,
        cell: (row) => (
          <Button size="sm" variant="secondary" onClick={() => onView(row)}>
            {t('actions.view')}
          </Button>
        ),
      },
    ],
    [t, dateFormatter, onView],
  );

  return (
    <DataTable<Announcement>
      data={announcements}
      columns={columns}
      rowKey={(row) => row.id}
      testId="admin-announcements"
      selectable
      pagination={pagination}
      isLoading={isLoading}
      error={error}
      emptyText={t('empty.title')}
      exportable
      exportFilename="admin-announcements"
    />
  );
}
